/**
 * Letter-first onboarding — delivery setup (letter tier and above).
 *
 * Two decisions: who receives it, and what releases it. Recipients come
 * from the user's keyholders (existing invitation flow — "The people you
 * trust" adds more; successors arrive with Level 2). Triggers v2 (owner
 * design 2026-09-03):
 *
 *   - after I'm gone — death-event-only grants. Two paths open them: the
 *     keyholder-initiated wellness check (alert, 7 silent days, second
 *     alert, 3 more — letters only), or a verified death certificate for
 *     accounts with successors. Optional wait after the event.
 *   - on a date I choose — living grants created now, content sealed
 *     server-side until the chosen day; grantees get an email that morning
 *   - only when I release it myself — intent recorded, nothing granted
 *     until the owner acts
 */
import { useFocusEffect, useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import DateTimePickerModal from 'react-native-modal-datetime-picker';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import {
  letterToLovedOnesService,
  DeliveryCandidate,
} from '../../services/letterToLovedOnesInfo';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('LetterDelivery');

type DeliveryRouteProp = RouteProp<{ params: { letterId: string } }, 'params'>;

const DELAY_OPTIONS: { label: string; hours: number }[] = [
  { label: 'Right away', hours: 0 },
  { label: '14 days after', hours: 14 * 24 },
  { label: '30 days after', hours: 30 * 24 },
  { label: '90 days after', hours: 90 * 24 },
  { label: '1 year after', hours: 365 * 24 },
];

export const LetterDeliveryScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<DeliveryRouteProp>();
  const { letterId } = route.params;

  const [candidates, setCandidates] = useState<
    { person: DeliveryCandidate; role: 'successor' | 'keyholder' }[]
  >([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [trigger, setTrigger] = useState<'eol' | 'manual' | 'date' | null>(null);
  const [delayHours, setDelayHours] = useState(0);
  const [releaseDate, setReleaseDate] = useState<Date | null>(null);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const load = async () => {
        try {
          const [letter, people] = await Promise.all([
            letterToLovedOnesService.getLetterToLovedOnesById(letterId),
            letterToLovedOnesService.getDeliveryCandidates(),
          ]);
          if (cancelled) return;
          // Keyholders only: letter delivery goes to the people you hand
          // this specific thing to. Successors arrive with Level 2.
          const merged = people.keyholders
            .map((person) => ({ person, role: 'keyholder' as const }))
            .filter((c) => c.person.email);
          setCandidates(merged);
          if (
            letter?.deliveryTrigger === 'eol' ||
            letter?.deliveryTrigger === 'manual' ||
            letter?.deliveryTrigger === 'date'
          ) {
            setTrigger(letter.deliveryTrigger);
            setDelayHours(letter.releaseDelayHours ?? 0);
            if (letter.deliveryTrigger === 'date' && letter.releaseAt) {
              setReleaseDate(new Date(letter.releaseAt));
            }
          }
        } catch (error) {
          logger.error('Failed to load delivery setup:', error);
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [letterId]),
  );

  const toggleSelected = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const canSave =
    trigger === 'manual' ||
    (trigger === 'eol' && selected.size > 0) ||
    (trigger === 'date' && selected.size > 0 && releaseDate != null);

  const handleSave = async () => {
    if (!canSave || !trigger || saving) return;
    try {
      setSaving(true);
      await letterToLovedOnesService.setDelivery(letterId, {
        trigger,
        releaseDelayHours: trigger === 'eol' ? delayHours : 0,
        releaseAt: trigger === 'date' && releaseDate ? releaseDate.toISOString() : undefined,
        granteeEmails: trigger === 'manual' ? [] : Array.from(selected),
      });
      const who = `${selected.size} ${selected.size === 1 ? 'person' : 'people'}`;
      const summary =
        trigger === 'manual'
          ? 'Nothing will be released until you release it yourself.'
          : trigger === 'date'
            ? `${who} will receive it on ${releaseDate?.toLocaleDateString() ?? 'the chosen date'}.`
            : `${who} will receive it ${
                delayHours === 0
                  ? "when you're gone."
                  : `${DELAY_OPTIONS.find((d) => d.hours === delayHours)?.label.toLowerCase() ?? ''} you're gone.`
              }`;
      Alert.alert('Delivery set', summary, [
        {
          text: 'Done',
          // Security options self-forward to LetterSaved when a decision
          // is already recorded — the prompt shows at most once.
          onPress: () =>
            navigation.navigate('SecurityOptions', { from: 'flow', letterId }),
        },
      ]);
    } catch (error: any) {
      logger.error('Failed to save delivery:', error);
      Alert.alert('Error', error?.message || 'Failed to save delivery settings.');
    } finally {
      setSaving(false);
    }
  };

  const displayName = (c: DeliveryCandidate) =>
    [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email;

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Delivery</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        {loading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color="#29BFC0" size="large" />
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.intro}>
              Two decisions and this is set: who gets it, and what opens it.
            </Text>

            <Text style={styles.sectionLabel}>WHO RECEIVES IT</Text>
            {candidates.length === 0 ? (
              <View style={styles.emptyCard}>
                <Text style={styles.emptyText}>
                  No trusted people yet. Add a successor or keyholder first —
                  they're the ones delivery can reach.
                </Text>
              </View>
            ) : (
              candidates.map(({ person, role }) => {
                const isSelected = selected.has(person.email);
                return (
                  <TouchableOpacity
                    key={`${role}-${person.email}`}
                    style={[styles.personCard, isSelected && styles.personCardSelected]}
                    onPress={() => toggleSelected(person.email)}
                    accessibilityRole="button"
                    accessibilityLabel={`${displayName(person)}, ${role}`}
                    accessibilityState={{ selected: isSelected }}
                  >
                    <View style={styles.personText}>
                      <Text style={styles.personName}>{displayName(person)}</Text>
                      <Text style={styles.personRole}>
                        {role === 'successor' ? 'Successor' : 'Keyholder'} · {person.email}
                      </Text>
                    </View>
                    <View style={[styles.checkbox, isSelected && styles.checkboxOn]}>
                      {isSelected && (
                        <Image
                          source={require('../../assets/icons/check.png')}
                          style={styles.checkboxIcon}
                        />
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })
            )}
            <TouchableOpacity
              style={styles.addPeopleButton}
              onPress={() => navigation.navigate('LetterPeople', { letterId })}
              accessibilityRole="button"
              accessibilityLabel="Add the people you trust"
            >
              <Text style={styles.addPeopleText}>+ Add the people you trust</Text>
            </TouchableOpacity>

            <Text style={styles.sectionLabel}>WHAT RELEASES IT</Text>
            <TouchableOpacity
              style={[styles.triggerCard, trigger === 'eol' && styles.triggerCardSelected]}
              onPress={() => setTrigger('eol')}
              accessibilityRole="button"
              accessibilityLabel="After I'm gone"
              accessibilityState={{ selected: trigger === 'eol' }}
            >
              <Text style={styles.triggerTitle}>After I'm gone</Text>
              <Text style={styles.triggerSub}>
                A keyholder asks us to check on you. We reach out by text and
                email — if we can't reach you for 10 days, your letter is
                delivered. Answering at any point stops it.
              </Text>
              {trigger === 'eol' && (
                <View style={styles.delayRow}>
                  {DELAY_OPTIONS.map((option) => (
                    <TouchableOpacity
                      key={option.hours}
                      style={[styles.delayChip, delayHours === option.hours && styles.delayChipOn]}
                      onPress={() => setDelayHours(option.hours)}
                      accessibilityRole="button"
                      accessibilityLabel={option.label}
                      accessibilityState={{ selected: delayHours === option.hours }}
                    >
                      <Text
                        style={[
                          styles.delayChipText,
                          delayHours === option.hours && styles.delayChipTextOn,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.triggerCard, trigger === 'date' && styles.triggerCardSelected]}
              onPress={() => {
                setTrigger('date');
                if (!releaseDate) setPickerVisible(true);
              }}
              accessibilityRole="button"
              accessibilityLabel="On a date I choose"
              accessibilityState={{ selected: trigger === 'date' }}
            >
              <Text style={styles.triggerTitle}>On a date I choose</Text>
              <Text style={styles.triggerSub}>
                A birthday, a wedding, a someday. It stays sealed until then.
              </Text>
              {trigger === 'date' && (
                <TouchableOpacity
                  style={styles.dateChip}
                  onPress={() => setPickerVisible(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Choose the delivery date"
                >
                  <Text style={styles.dateChipText}>
                    {releaseDate ? releaseDate.toLocaleDateString() : 'Choose the date'}
                  </Text>
                </TouchableOpacity>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.triggerCard, trigger === 'manual' && styles.triggerCardSelected]}
              onPress={() => setTrigger('manual')}
              accessibilityRole="button"
              accessibilityLabel="Only when I release it myself"
              accessibilityState={{ selected: trigger === 'manual' }}
            >
              <Text style={styles.triggerTitle}>Only when I release it myself</Text>
              <Text style={styles.triggerSub}>Nothing happens without you.</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.saveButton, (!canSave || saving) && styles.disabled]}
              onPress={handleSave}
              disabled={!canSave || saving}
              accessibilityRole="button"
              accessibilityLabel="Save delivery settings"
              accessibilityState={{ disabled: !canSave || saving }}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>Save delivery</Text>
              )}
            </TouchableOpacity>

            <View style={styles.bottomPadding} />
          </ScrollView>
        )}
      </View>

      <DateTimePickerModal
        isVisible={pickerVisible}
        mode="date"
        minimumDate={new Date(Date.now() + 24 * 3600 * 1000)}
        date={releaseDate ?? new Date(Date.now() + 24 * 3600 * 1000)}
        onConfirm={(picked) => {
          setPickerVisible(false);
          setReleaseDate(picked);
        }}
        onCancel={() => setPickerVisible(false)}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    paddingHorizontal: ms(16),
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  intro: {
    fontSize: ms(15, 0.3),
    lineHeight: ms(22, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
    marginBottom: ms(16),
  },
  sectionLabel: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#A9A9A9',
    letterSpacing: 1,
    marginBottom: ms(8),
    marginTop: ms(8),
  },
  emptyCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(8),
  },
  emptyText: {
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#6B7280',
  },
  personCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(2),
    borderColor: '#E5E5E5',
    padding: ms(14),
    marginBottom: ms(8),
  },
  personCardSelected: {
    borderColor: '#29BFC0',
  },
  personText: {
    flex: 1,
  },
  personName: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  personRole: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  checkbox: {
    width: s(24),
    height: s(24),
    borderRadius: ms(6),
    borderWidth: ms(2),
    borderColor: '#D8DBDD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOn: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  checkboxIcon: {
    width: s(14),
    height: s(14),
    tintColor: '#FFFFFF',
  },
  addPeopleButton: {
    alignItems: 'center',
    paddingVertical: ms(11),
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    marginBottom: ms(10),
  },
  addPeopleText: {
    color: '#29BFC0',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  triggerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(2),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(10),
  },
  triggerCardSelected: {
    borderColor: '#29BFC0',
  },
  triggerTitle: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  triggerSub: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
    marginTop: ms(4),
  },
  dateChip: {
    alignSelf: 'flex-start',
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: ms(14),
    paddingVertical: ms(8),
    marginTop: ms(12),
  },
  dateChipText: {
    fontSize: ms(13, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  delayRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: ms(8),
    marginTop: ms(12),
  },
  delayChip: {
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#D8DBDD',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: ms(12),
    paddingVertical: ms(7),
  },
  delayChipOn: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  delayChipText: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#2C4364',
  },
  delayChipTextOn: {
    color: '#FFFFFF',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
    marginTop: ms(8),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  disabled: {
    opacity: 0.5,
  },
  bottomPadding: {
    height: s(40),
  },
});

export default LetterDeliveryScreen;
